import { Badge, FutureListPage } from "@administrate/piston-ux";
import { Fragment, FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ColorOptions } from "@administrate/piston-ux/lib/types";
import moment from "moment";
import { RequestTraining } from "../../components/RequestTraining";
import { useAlreadyRegisteredEventIds } from "../../hooks/useAlreadyRegisteredEventIds";
import { useBookerIntention } from "../../hooks/useBookerIntention";
import { useWebLinkQuery } from "../../hooks/weblink";
import { usePortal } from "../../providers/PortalProvider";
import { useViewer } from "../../providers/ViewerProvider";
import {
  PRIVATE_TRAINING_COORDINATOR_COUNT,
  PRIVATE_TRAINING_SELF_COUNT,
} from "../../queries/privateTraining";
import { CartWidget } from "../Cart/CartWidget";
import { BookerIntention } from "../Order/BookingPage";
import { useCatalogFilters } from "./CatalogFilterContext";
import { buildEventFilters } from "./privateTrainingFilters";

type CatalogPageProps = {
  tab: "find" | "browse" | "private";
  title: string;
};

export const CatalogPage: FunctionComponent<
  React.PropsWithChildren<CatalogPageProps>
> = ({ tab, title, children }) => {
  const { t } = useTranslation();
  const { showPrivateTraining } = usePortal();
  const [bookerIntention] = useBookerIntention();
  const [query, queryName] =
    bookerIntention === BookerIntention.Coordinating
      ? [PRIVATE_TRAINING_COORDINATOR_COUNT, "privateTrainingForCoordinator"]
      : [PRIVATE_TRAINING_SELF_COUNT, "privateTraining"];
  const { filters } = useCatalogFilters();
  const { registeredEventIds, loading: registeredEventIdsLoading } =
    useAlreadyRegisteredEventIds();
  const { viewer } = useViewer();
  const viewerTimeZone = viewer?.timeZoneName ?? moment.tz.guess();
  const { loading, data } = useWebLinkQuery(query, {
    variables: {
      filters: buildEventFilters(
        filters,
        viewerTimeZone,
        registeredEventIds,
        bookerIntention,
      ),
    },
    skip: registeredEventIdsLoading || !showPrivateTraining,
  });
  const privateTrainingCount = useMemo(
    () => (!loading && data ? data[queryName].pageInfo.totalRecords : null),
    [loading, data, queryName],
  );

  return (
    <FutureListPage
      title={title}
      navigation={[
        {
          label: t("browseCatalog"),
          link: (
            <Link to={`/catalog/${bookerIntention}`}>{t("browseCatalog")}</Link>
          ),
          active: tab === "browse",
        },
        {
          label: t("allDates"),
          link: (
            <Link to={`/catalog/${bookerIntention}/find`}>{t("allDates")}</Link>
          ),
          active: tab === "find",
        },
        {
          label: t("privateTraining"),
          link: (
            <Link to={`/catalog/${bookerIntention}/private`}>
              {t("privateTraining")} &nbsp;
              {privateTrainingCount ? (
                <Badge color={ColorOptions.Red} text={privateTrainingCount} />
              ) : undefined}
            </Link>
          ),
          active: tab === "private",
          hidden: !showPrivateTraining,
        },
      ]}
      extra={
        <Fragment>
          {bookerIntention === BookerIntention.Self && <CartWidget />}
          <RequestTraining />
        </Fragment>
      }
      type={tab === "browse" ? "grid" : "table"}
    >
      {children}
    </FutureListPage>
  );
};
